import {
  DateSelect,
  Input,
  MultiSelect,
  MultiSelectOptionType,
  Pill,
  Select,
  Stack,
  Table,
  TableColumns,
  TagsInput,
} from '@sixfold/common-ui';
import { TpRtvWebhookTopicRuntype } from '@sixfold/public-api-interface';
import { notNil, safeParseDate } from '@sixfold/typed-primitives';
import React from 'react';

import { FormattedDate } from '../../../components/date_formatting/formatted_date';
import { isValidEmail } from '../../../lib/form';
import {
  CompanyWebhookSubscriptionInfo,
  CompanyWebhookSubscriptionStatus,
  UpdateCompanyWebhookSubscriptionStatus,
  WebhookPayloadContentType,
} from '../../../lib/graphql';
import { parseDiscriminator, WebhookDiscriminatorField } from '../webhook_discriminator_field';
import { WebhookHeaderField } from '../webhook_header_field';
import type { EditState, ValidationResult } from './webhook_subscription_form';

import styles from '../../containers/styles.module.css';

interface WebhookSubscriptionTableProps {
  isCreateMode: boolean;
  isEditing: boolean;
  hasAttemptedSave: boolean;
  editState: EditState;
  validation: ValidationResult;
  subscription: CompanyWebhookSubscriptionInfo | null;
  onEditStateChange: (updater: (state: EditState) => EditState) => void;
}

const STATUS_OPTIONS: { value: UpdateCompanyWebhookSubscriptionStatus; label: string }[] = [
  { value: UpdateCompanyWebhookSubscriptionStatus.WORKING, label: 'WORKING' },
  { value: UpdateCompanyWebhookSubscriptionStatus.PAUSED, label: 'PAUSED' },
];

const PAYLOAD_CONTENT_TYPE_OPTIONS: { value: WebhookPayloadContentType; label: string }[] = [
  { value: WebhookPayloadContentType.JSON, label: 'JSON' },
  { value: WebhookPayloadContentType.XML, label: 'XML' },
];

const TP_RTV_TOPICS = TpRtvWebhookTopicRuntype.alternatives.map((literal) => literal.value);
const TOPIC_OPTIONS = TP_RTV_TOPICS.map((topic) => ({ value: topic, label: topic }));

export const WEBHOOK_STATUS_COLORS: { [key in CompanyWebhookSubscriptionStatus]: 'green' | 'yellow' | 'red' } = {
  [CompanyWebhookSubscriptionStatus.WORKING]: 'green',
  [CompanyWebhookSubscriptionStatus.PAUSED]: 'yellow',
  [CompanyWebhookSubscriptionStatus.BROKEN]: 'red',
};

const WEBHOOK_STATUS_PILL_KIND: { [key in CompanyWebhookSubscriptionStatus]: 'success' | 'warning' | 'danger' } = {
  [CompanyWebhookSubscriptionStatus.WORKING]: 'success',
  [CompanyWebhookSubscriptionStatus.PAUSED]: 'warning',
  [CompanyWebhookSubscriptionStatus.BROKEN]: 'danger',
};

type WebhookFieldRow = {
  id: string;
  label: React.ReactNode;
  value: React.ReactNode;
};

const webhookFieldColumns: TableColumns<WebhookFieldRow> = [
  {
    id: 'label',
    title: undefined,
    width: '25%',
  },
  {
    id: 'value',
    title: undefined,
    cellContent: ({ value }) => value,
  },
];

export function WebhookSubscriptionTable({
  isCreateMode,
  isEditing,
  hasAttemptedSave,
  editState,
  validation,
  subscription,
  onEditStateChange,
}: WebhookSubscriptionTableProps) {
  const handleEmailChange = (values: MultiSelectOptionType[]) => {
    const validatedEmails = values.map((value) => ({ ...value, invalid: !isValidEmail(value.value) }));
    onEditStateChange((s) => ({ ...s, contactEmails: validatedEmails }));
  };

  const rows: WebhookFieldRow[] = [];

  if (!isCreateMode && notNil(subscription)) {
    rows.push({
      id: 'subscription-id',
      label: 'Subscription ID',
      value: subscription.subscriptionId,
    });
  }

  if (!isCreateMode && notNil(subscription)) {
    rows.push({
      id: 'status',
      label: 'Status',
      value: isEditing ? (
        <Select
          options={STATUS_OPTIONS}
          value={{ value: editState.status, label: editState.status }}
          onChange={(option) => {
            if (notNil(option)) {
              onEditStateChange((s) => ({ ...s, status: option.value }));
            }
          }}
          className={styles.webhookStatusSelect}
        />
      ) : (
        <Pill kind={WEBHOOK_STATUS_PILL_KIND[subscription.status]}>{subscription.status}</Pill>
      ),
    });
  }

  rows.push({
    id: 'url',
    label: 'URL',
    value: isEditing ? (
      <Input
        label="URL"
        name="url"
        value={editState.url}
        onChange={(e) => onEditStateChange((s) => ({ ...s, url: e.value }))}
        className={styles.fullWidthInput}
        errorMessage={hasAttemptedSave && validation.isUrlEmpty ? 'URL is required' : undefined}
      />
    ) : (
      subscription?.url
    ),
  });

  rows.push({
    id: 'payload-content-type',
    label: 'Payload Content Type',
    value: isEditing ? (
      <Select
        options={PAYLOAD_CONTENT_TYPE_OPTIONS}
        value={{ value: editState.payloadContentType, label: editState.payloadContentType }}
        onChange={(option) => {
          if (notNil(option)) {
            onEditStateChange((s) => ({ ...s, payloadContentType: option.value }));
          }
        }}
        className={styles.webhookPayloadTypeSelect}
      />
    ) : (
      <Pill kind="neutral">{subscription?.payloadContentType}</Pill>
    ),
  });

  rows.push({
    id: 'expiration-date',
    label: 'Expiration Date',
    value: isEditing ? (
      <DateSelect
        label="Expiration Date"
        labelPosition="hidden"
        name="expirationDate"
        value={safeParseDate(editState.expirationDate)}
        onChange={(date) => {
          if (notNil(date)) {
            onEditStateChange((s) => ({ ...s, expirationDate: date.toISOString() }));
          }
        }}
        className={styles.webhookDateSelect}
      />
    ) : (
      <FormattedDate date={subscription?.expirationDate} />
    ),
  });

  rows.push({
    id: 'contact-emails',
    label: 'Contact Emails',
    value: isEditing ? (
      <TagsInput
        label="Contact Emails"
        labelPosition="hidden"
        size="small"
        value={editState.contactEmails}
        onChange={handleEmailChange}
        createOnBlur
        placeholder="Enter email address"
        errorMessage={validation.isEmailInvalid ? 'Please enter valid email addresses' : undefined}
      />
    ) : (subscription?.contactEmails.length ?? 0) > 0 ? (
      subscription?.contactEmails.join(', ')
    ) : (
      '-'
    ),
  });

  rows.push({
    id: 'topics',
    label: 'Topics',
    value: isEditing ? (
      <>
        <MultiSelect
          options={TOPIC_OPTIONS}
          value={editState.topics.map((t) => ({ value: t, label: t }))}
          onChange={(options) => onEditStateChange((s) => ({ ...s, topics: options.map((o) => o.value) }))}
          placeholder="Select topics"
          className={styles.webhookTopicsDropdown}
        />
        {hasAttemptedSave && validation.hasNoTopics && (
          <p className={styles.errorText}>At least one topic is required</p>
        )}
      </>
    ) : (
      <Stack gap="xsmall" flexWrap="wrap" direction="row">
        {subscription?.topics.map((topic) => (
          <Pill key={topic} kind="info">
            {topic}
          </Pill>
        ))}
      </Stack>
    ),
  });

  rows.push({
    id: 'headers',
    label: 'Headers',
    value: (
      <WebhookHeaderField
        key={isEditing.toString()}
        isEditing={isEditing}
        headers={isEditing ? editState.headers : (subscription?.headers ?? {})}
        onHeadersChange={(h) => onEditStateChange((s) => ({ ...s, headers: h }))}
      />
    ),
  });

  rows.push({
    id: 'discriminator',
    label: 'Discriminator',
    value: (
      <>
        <WebhookDiscriminatorField
          key={isEditing.toString()}
          isEditing={isEditing}
          platformId={editState.platformId}
          discriminator={parseDiscriminator(subscription?.discriminator ?? undefined)}
          onPlatformIdChange={(platformId) => onEditStateChange((s) => ({ ...s, platformId }))}
        />
        {isEditing && hasAttemptedSave && validation.hasDiscriminatorError && (
          <p className={styles.errorText}>Platform ID is required when topics are selected</p>
        )}
      </>
    ),
  });

  if (!isCreateMode && notNil(subscription?.brokenReason)) {
    rows.push({
      id: 'broken-reason',
      label: 'Broken Reason',
      value: subscription?.brokenReason,
    });
  }

  if (!isCreateMode && notNil(subscription)) {
    rows.push(
      {
        id: 'created',
        label: 'Created',
        value: (
          <>
            <FormattedDate date={subscription.createdAt} /> by user with ID: {subscription.createdByUserId ?? '-'}
          </>
        ),
      },
      {
        id: 'updated',
        label: 'Updated',
        value: (
          <>
            <FormattedDate date={subscription.updatedAt} /> by user with ID: {subscription.updatedByUserId ?? '-'}
          </>
        ),
      },
      {
        id: 'last-status-change',
        label: 'Last Status Change',
        value: <FormattedDate date={subscription.lastStatusChange} />,
      },
      {
        id: 'subscription-updated-at',
        label: 'Subscription Updated At',
        value: <FormattedDate date={subscription.subscriptionUpdatedAt} />,
      },
    );
  }

  return <Table data={rows} columns={webhookFieldColumns} />;
}
